import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { History, User, Package, Building2, ChevronRight, Download } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { getScanTargetLabel } from "../services/codeRecognitionService";
import { scanService } from "../services/scanService";
import { companiesService } from "../services/companiesService";
import { catalogService } from "../services/catalogService";
import { productsService } from "../services/productsService";
import { leadsService } from "../services/leadsService";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { EmptyState } from "../components/EmptyState";
import type { ScanLog } from "../types";
import type { Company, Lead, Representative } from "../types";
import { downloadCsv } from "../utils/downloadCsv";
import { toast } from "sonner";
import { markLabel } from "../utils/contactMarks";
import { SkeletonList } from "../components/SkeletonList";
import { formatAnnotationNotesPreview } from "../utils/annotationNotes";

function formatDate(
  iso: string,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("history.just_now");
  if (mins < 60) return t("history.minutes_ago", { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("history.hours_ago", { n: hours });
  return d.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ScanHistoryItem extends ScanLog {
  title: string;
  subtitle: string;
  icon: typeof Building2;
  link: string;
  /** Flattened fields for full CSV export */
  exportCompanyName: string;
  exportBooth: string;
  exportHall: string;
  exportPhone: string;
  exportEmail: string;
  exportWebsite: string;
  exportPersonName: string;
  exportPosition: string;
  exportProductName: string;
  exportCategory: string;
}

type HistoryTab = "mine" | "company";

function companyFromScan(scan: ScanLog) {
  return scan.company;
}

type ResolveCache = {
  repsByCompany: Map<string, Promise<Representative[]>>;
  companies: Map<string, Promise<Company | undefined>>;
};

async function resolveScanHistoryItem(
  scan: ScanLog,
  selectedEventId: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  cache: ResolveCache,
): Promise<ScanHistoryItem> {
  const targetType = scan.scanTargetType || "company";
  const targetId = scan.scanTargetId || scan.companyId || "";
  const embedded = companyFromScan(scan);

  const loadReps = (companyId: string) => {
    let p = cache.repsByCompany.get(companyId);
    if (!p) {
      p = catalogService.listRepresentatives(selectedEventId, companyId).catch(() => []);
      cache.repsByCompany.set(companyId, p);
    }
    return p;
  };

  const loadCompany = (companyId: string) => {
    let p = cache.companies.get(companyId);
    if (!p) {
      p = companiesService.getById(companyId, selectedEventId).catch(() => undefined);
      cache.companies.set(companyId, p);
    }
    return p;
  };

  const baseExport = {
    exportCompanyName: embedded?.name || "",
    exportBooth: embedded?.booth || "",
    exportHall: embedded?.hall || "",
    exportPhone: embedded?.phone || scan.visitor?.phone || "",
    exportEmail: embedded?.email || scan.visitor?.email || "",
    exportWebsite: embedded?.website || "",
    exportPersonName: "",
    exportPosition: "",
    exportProductName: "",
    exportCategory: embedded?.category || "",
  };

  const fallback = (title: string, subtitle: string, icon: typeof Building2, link: string): ScanHistoryItem => ({
    ...scan,
    title,
    subtitle,
    icon,
    link,
    ...baseExport,
  });

  switch (targetType) {
    case "visitor": {
      const linked = scan.linkedRepresentative;
      if (linked) {
        return {
          ...fallback(
            linked.fullName,
            [linked.position, scan.visitor?.companyName, scan.visitor?.phone].filter(Boolean).join(" · ") ||
              t("visitor.contact_saved"),
            User,
            `/representatives/${linked.id}`,
          ),
          exportPersonName: linked.fullName,
          exportPosition: linked.position || "",
          exportCompanyName: scan.visitor?.companyName || embedded?.name || "",
          exportPhone: scan.visitor?.phone || "",
          exportEmail: scan.visitor?.email || "",
        };
      }
      const visitor = scan.visitor;
      const title = visitor?.fullName || t("history.anonymous_visitor");
      const subtitle =
        [visitor?.companyName, visitor?.city, visitor?.activityType, visitor?.position, visitor?.phone]
          .filter(Boolean)
          .join(" · ") || t("visitor.contact_saved");
      return {
        ...fallback(title, subtitle, User, targetId ? `/visitors/${targetId}` : "/history"),
        exportPersonName: visitor?.fullName || "",
        exportPosition: visitor?.position || "",
        exportCompanyName: visitor?.companyName || "",
        exportPhone: visitor?.phone || "",
        exportEmail: visitor?.email || "",
      };
    }
    case "representative":
    case "badge": {
      const companyId = scan.companyId || embedded?.id;
      let repName = "";
      let repPosition = "";
      let companyName = embedded?.name || "";
      let booth = embedded?.booth || "";
      let category = embedded?.category || "";
      let phone = embedded?.phone || "";
      let email = embedded?.email || "";
      let website = embedded?.website || "";

      if (targetId) {
        const fromPack = await catalogService.findRepresentative(selectedEventId, targetId);
        if (fromPack) {
          repName = fromPack.fullName;
          repPosition = fromPack.position || "";
          phone = fromPack.phone || phone;
          email = fromPack.email || email;
        }
      }
      if (!repName && companyId) {
        const reps = await loadReps(companyId);
        const rep = reps.find((r) => r.id === targetId);
        if (rep) {
          repName = rep.fullName;
          repPosition = rep.position || "";
          phone = rep.phone || phone;
          email = rep.email || email;
        }
      }
      if (!companyName && companyId) {
        const company = await loadCompany(companyId);
        if (company) {
          companyName = company.name;
          booth = company.booth || booth;
          category = company.category || category;
          phone = phone || company.contact?.phone || "";
          email = email || company.contact?.email || "";
          website = website || company.website || "";
        }
      }

      const title = repName || getScanTargetLabel(targetType, t);
      const subtitle =
        [repPosition, companyName, booth ? t("map.booth", { booth }) : ""].filter(Boolean).join(" · ") ||
        targetId;
      return {
        ...fallback(title, subtitle, User, targetId ? `/representatives/${targetId}` : "/history"),
        exportCompanyName: companyName,
        exportBooth: booth,
        exportHall: embedded?.hall || "",
        exportPhone: phone,
        exportEmail: email,
        exportWebsite: website,
        exportPersonName: repName,
        exportPosition: repPosition,
        exportCategory: category,
      };
    }
    case "product": {
      const companyId = scan.companyId || embedded?.id;
      let productName = "";
      let productCategory = "";
      let companyName = embedded?.name || "";
      let booth = embedded?.booth || "";
      let phone = embedded?.phone || "";
      let email = embedded?.email || "";
      let website = embedded?.website || "";

      if (companyId && targetId) {
        const product = await productsService
          .getById(selectedEventId, companyId, targetId)
          .catch(() => undefined);
        if (product) {
          productName = product.name;
          productCategory = product.category || "";
        }
      }
      if (companyId && !companyName) {
        const company = await loadCompany(companyId);
        if (company) {
          companyName = company.name;
          booth = company.booth || booth;
          phone = company.contact?.phone || phone;
          email = company.contact?.email || email;
          website = company.website || website;
        }
      }

      const title = productName || getScanTargetLabel("product", t);
      const subtitle =
        [productCategory, companyName, booth ? t("map.booth", { booth }) : ""].filter(Boolean).join(" · ") ||
        targetId;
      return {
        ...fallback(title, subtitle, Package, targetId ? `/products/${targetId}` : "/history"),
        exportCompanyName: companyName,
        exportBooth: booth,
        exportHall: embedded?.hall || "",
        exportPhone: phone,
        exportEmail: email,
        exportWebsite: website,
        exportProductName: productName,
        exportCategory: productCategory || embedded?.category || "",
      };
    }
    case "company":
    case "booth":
    default: {
      const companyId = scan.companyId || targetId || embedded?.id || "";
      let company = embedded
        ? {
            id: embedded.id,
            name: embedded.name,
            booth: embedded.booth || "",
            category: embedded.category || "",
            hall: embedded.hall || "",
            contact: { phone: embedded.phone, email: embedded.email },
            website: embedded.website,
          }
        : undefined;
      if (!company?.name && companyId) {
        company = await loadCompany(companyId);
      }
      const title = company?.name || getScanTargetLabel(targetType, t) || targetId || t("history.title");
      const booth = company?.booth || "";
      const subtitle =
        [booth ? t("map.booth", { booth }) : "", company?.category, company?.hall]
          .filter(Boolean)
          .join(" · ") || targetId;
      return {
        ...fallback(
          title,
          subtitle,
          Building2,
          companyId ? `/companies/${companyId}` : "/history",
        ),
        exportCompanyName: company?.name || "",
        exportBooth: booth,
        exportHall: company?.hall || "",
        exportPhone: company?.contact?.phone || "",
        exportEmail: company?.contact?.email || "",
        exportWebsite: company?.website || "",
        exportCategory: company?.category || "",
      };
    }
  }
}

export function HistoryPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectedEventId, eventRole, exhibitorCompanyId } = useAppStore();
  const isExhibitor = eventRole === "exhibitor" && Boolean(exhibitorCompanyId);
  const [tab, setTab] = useState<HistoryTab>("mine");
  const [items, setItems] = useState<ScanHistoryItem[]>([]);
  const [companyLeads, setCompanyLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isExhibitor) setTab("mine");
  }, [isExhibitor]);

  useEffect(() => {
    if (!selectedEventId) {
      setItems([]);
      return;
    }
    if (tab === "company") return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const scans = await scanService.getHistory(selectedEventId);
        const cache: ResolveCache = {
          repsByCompany: new Map(),
          companies: new Map(),
        };
        const resolved = await Promise.all(
          scans.map((scan) => resolveScanHistoryItem(scan, selectedEventId, t, cache)),
        );

        if (!cancelled) {
          setItems(
            resolved.sort(
              (a, b) => new Date(b.scannedAt).getTime() - new Date(a.scannedAt).getTime(),
            ),
          );
        }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language, selectedEventId, tab]);

  useEffect(() => {
    if (!selectedEventId || !isExhibitor || tab !== "company" || !exhibitorCompanyId) {
      setCompanyLeads([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { leads } = await leadsService.listByCompany(
          selectedEventId,
          exhibitorCompanyId,
          "visitor",
        );
        if (!cancelled) {
          setCompanyLeads(
            leads.sort(
              (a, b) => new Date(b.scannedAt).getTime() - new Date(a.scannedAt).getTime(),
            ),
          );
        }
      } catch {
        if (!cancelled) setCompanyLeads([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, isExhibitor, tab, exhibitorCompanyId]);

  const emptyMine = !loading && tab === "mine" && items.length === 0;
  const emptyCompany = !loading && tab === "company" && companyLeads.length === 0;

  const handleExport = () => {
    if (tab === "mine") {
      if (!items.length) {
        toast.error(t("history.export_empty"));
        return;
      }
      downloadCsv(
        `mvk-scans-${new Date().toISOString().slice(0, 10)}.csv`,
        [
          "scanned_at",
          "type",
          "type_label",
          "title",
          "company",
          "booth",
          "hall",
          "category",
          "person",
          "position",
          "product",
          "phone",
          "email",
          "website",
          "notes",
          "tags",
        ],
        items.map((item) => [
          item.scannedAt || "",
          item.scanTargetType || "",
          getScanTargetLabel(item.scanTargetType, t),
          item.title || "",
          item.exportCompanyName || "",
          item.exportBooth || "",
          item.exportHall || "",
          item.exportCategory || "",
          item.exportPersonName || "",
          item.exportPosition || "",
          item.exportProductName || "",
          item.exportPhone || "",
          item.exportEmail || "",
          item.exportWebsite || "",
          formatAnnotationNotesPreview(item.notes, item.tags ?? [], "\n"),
          (item.tags ?? []).map((tag) => markLabel(tag, t)).join(", "),
        ]),
      );
      toast.success(t("history.export_ok"));
      return;
    }
    if (!companyLeads.length) {
      toast.error(t("history.export_empty"));
      return;
    }
    downloadCsv(
      `mvk-company-leads-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        "scanned_at",
        "visitor",
        "company",
        "position",
        "phone",
        "email",
        "scan_type",
        "status",
        "notes",
        "tags",
        "lead_id",
      ],
      companyLeads.map((lead) => [
        lead.scannedAt || "",
        lead.visitorName || t("history.anonymous_visitor"),
        lead.visitorCompany || "",
        lead.visitorPosition || "",
        lead.visitorPhone || "",
        lead.visitorEmail || "",
        getScanTargetLabel(lead.scanType, t),
        lead.status || "",
        formatAnnotationNotesPreview(lead.notes, lead.tags ?? [], "\n"),
        (lead.tags ?? []).map((tag) => markLabel(tag, t)).join(", "),
        lead.id || "",
      ]),
    );
    toast.success(t("history.export_ok"));
  };

  const canExport =
    (tab === "mine" && items.length > 0) || (tab === "company" && companyLeads.length > 0);

  return (
    <>
      <PageHeader
        title={t("history.title")}
        back
        backTo="/my"
        right={
          canExport ? (
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/80 border border-gray-200 text-gray-700"
              aria-label={t("history.export")}
              title={t("history.export")}
            >
              <Download size={18} />
            </button>
          ) : undefined
        }
      />
      <PageShell>
        {isExhibitor ? (
          <div className="mt-3 flex gap-2 p-1 rounded-xl bg-gray-100">
            {(
              [
                { id: "mine" as const, label: t("history.tab_mine") },
                { id: "company" as const, label: t("history.tab_company") },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTab(opt.id)}
                className={`flex-1 h-9 rounded-lg text-[13px] font-medium transition-colors ${
                  tab === opt.id
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        ) : null}

        {loading && <SkeletonList count={6} variant="row" className="mt-4" />}

        {emptyMine ? (
          <EmptyState
            icon={<History size={32} />}
            title={t("history.empty_title")}
            description={t("history.empty_desc")}
            action={
              <button
                onClick={() => navigate("/scan")}
                className="px-5 h-10 rounded-xl bg-indigo-500 text-white text-[14px] active:bg-indigo-600 transition-colors"
              >
                {t("history.scan_cta")}
              </button>
            }
          />
        ) : null}

        {emptyCompany ? (
          <EmptyState
            icon={<Building2 size={32} />}
            title={t("history.empty_company_title")}
            description={t("history.empty_company_desc")}
          />
        ) : null}

        {!loading && tab === "mine" && items.length > 0 ? (
          <div className="mt-4 space-y-2">
            {items.map((item, i) => {
              const Icon = item.icon;
              const targetLabel = item.linkedRepresentative
                ? t("visitor.representative")
                : getScanTargetLabel(item.scanTargetType || "company", t);
              const notesPreview = formatAnnotationNotesPreview(
                item.notes,
                item.tags ?? [],
              );

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                >
                  <GlassCard
                    variant="default"
                    padding="sm"
                    className="cursor-pointer active:scale-[0.98] transition-transform"
                    onClick={() => {
                      if (item.link) navigate(item.link);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-[14px] text-gray-900 truncate">{item.title}</p>
                          <span className="text-[11px] text-gray-400 flex-shrink-0 ml-2">
                            {formatDate(item.scannedAt, i18n.language, t)}
                          </span>
                        </div>
                        <p className="text-[12px] text-gray-500 mt-0.5 truncate">{item.subtitle}</p>
                        {notesPreview ? (
                          <p className="text-[12px] text-gray-700 mt-1.5 line-clamp-2 whitespace-pre-wrap">
                            {notesPreview}
                          </p>
                        ) : null}
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="text-[11px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                            {targetLabel}
                          </span>
                          {item.codeType && (
                            <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full uppercase">
                              {item.codeType}
                            </span>
                          )}
                          {(item.tags ?? []).slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"
                            >
                              {markLabel(tag, t)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1" />
                    </div>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && tab === "company" && companyLeads.length > 0 ? (
          <div className="mt-4 space-y-2">
            {companyLeads.map((lead, i) => (
              <motion.div
                key={lead.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.3 }}
              >
                <GlassCard
                  variant="default"
                  padding="sm"
                  className="cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => navigate(`/exhibitor/leads/${lead.id}`)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <User size={20} className="text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[14px] text-gray-900 truncate">
                          {lead.visitorName || t("history.anonymous_visitor")}
                        </p>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">
                          {formatDate(lead.scannedAt, i18n.language, t)}
                        </span>
                      </div>
                      <p className="text-[12px] text-gray-500 mt-0.5 truncate">
                        {getScanTargetLabel(lead.scanType, t)} · {lead.status}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1" />
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        ) : null}
      </PageShell>
    </>
  );
}
